import React, { useMemo } from "react";
import { Box, Stack, Typography, CircularProgress, Alert } from "@mui/material";
import moment from "moment";
import { shiftPalette, classifyShift, formatShiftClock } from "./shiftHelpers";
import { designWeights } from "services/constants/designTokens";

// Weekly view — one styled row per day for the selected week, using the same
// type classification/colours as the monthly calendar. weeklyShiftList is the
// raw daily_user_shift_assignments payload (array of per-user wrappers, each
// with a `shifts` array), flattened here.
export const Weekly = ({ loading, weeklyShiftList }) => {
  const days = useMemo(() => {
    if (!Array.isArray(weeklyShiftList)) return [];
    return weeklyShiftList.flatMap((wrapper) => wrapper?.shifts || []);
  }, [weeklyShiftList]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (days.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No shifts scheduled for this week.
      </Alert>
    );
  }

  return (
    <Box>
      {days.map((shift, idx) => {
        const m = moment(shift.date, "DD-MM-YYYY");
        const category = classifyShift(shift.shift_name, shift.is_weekoff);
        const hasTime = shift.shift_start_time && shift.shift_end_time;
        return (
          <Stack
            key={`${shift.date}-${idx}`}
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{
              py: 1.5,
              px: 1,
              borderBottom:
                idx !== days.length - 1 ? `1px solid ${shiftPalette.border}` : "none",
            }}
          >
            <Box sx={{ width: 64, textAlign: "center", flexShrink: 0 }}>
              <Typography sx={{ fontSize: 22, fontWeight: designWeights.heading, color: shiftPalette.text, lineHeight: 1 }}>
                {m.isValid() ? m.format("DD") : "--"}
              </Typography>
              <Typography variant="caption" sx={{ color: shiftPalette.subText }}>
                {m.isValid() ? m.format("ddd, MMM") : ""}
              </Typography>
            </Box>

            {category ? (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  px: 2,
                  py: 1.25,
                  bgcolor: `${category.color}14`,
                  border: `1px solid ${category.color}33`,
                }}
              >
                <category.icon sx={{ fontSize: 18, color: category.color }} />
                <Box>
                  <Typography sx={{ fontWeight: designWeights.semibold, color: category.color }}>
                    {/* Real assigned shift name (e.g. "Morning Shift",
                    "Rotational Shift") -- classifyShift only buckets
                    Night/Flexible/Week Off/Holiday specially and lumps
                    every other shift into a generic "General Shift"
                    label, which used to be shown here instead of the
                    actual name. */}
                    {shift.shift_name || category.label}
                  </Typography>
                  {hasTime && (
                    <Typography variant="caption" sx={{ color: shiftPalette.subText }}>
                      {formatShiftClock(shift.shift_start_time)}
                      {" - "}
                      {formatShiftClock(shift.shift_end_time)}
                    </Typography>
                  )}
                </Box>
              </Stack>
            ) : (
              <Box sx={{ flex: 1, px: 2, py: 1.25, color: shiftPalette.subText }}>
                <Typography variant="body2">Not Joined</Typography>
              </Box>
            )}
          </Stack>
        );
      })}
    </Box>
  );
};
